import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Radio, RadioGroup } from 'react-aria-components';
import {
  DEFAULT_BROWSER_ENGINE,
  EXPLORATORY_EFFORT_MINUTES,
  EXPLORATORY_EFFORT_VALUES,
  FLAVOR_CAPABILITIES,
  type ExploratoryEffort,
  type BrowserEngine,
  type BrowserViewportPreset,
  type ReviewFlavor,
} from '@chatsdet/types';
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  TextArea,
  TextField,
  Text,
  Flex,
  Box,
  Button,
  Select,
  cn,
  type SelectOption,
} from '@chatsdet/ui';
import { useAuth } from '../../../contexts/AuthContext';
import { useOrgPropertySelection } from '../../../hooks/useOrgPropertySelection';
import { CredentialSelect } from '../../../ui/components/CredentialSelect';
import { useStartExploratoryTest, type ContextDocument } from '../useExploratoryTests';
import { ContextLibraryField } from './ContextLibraryField';
import { useNavigate } from '@tanstack/react-router';
import { API_URL } from '../../../config';
import { BrowserEnginePicker } from '../../../ui/components/BrowserEnginePicker';
import { ViewportPicker } from '../../../ui/components/ViewportPicker';
import {
  WEB_REVIEW_FLAVOR_LIST,
  getWebReviewFlavor,
  type ReviewFlavorDetailPath,
} from '../flavors';
import {
  ADHOC_TEST_INSTRUCTIONS_MAX_LENGTH,
  getExploratoryInstructionLength,
  getExploratoryInstructionsError,
  getExploratoryInstructionsLimitLabel,
} from '../validation';

type StartExploratoryTestDialogProps = {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  /** Initial review flavor. Defaults to 'functional'. */
  defaultFlavor?: ReviewFlavor;
  /** Hide the Mode toggle and pin to `defaultFlavor` (used by the Labs UX Review entry). */
  lockFlavor?: boolean;
};

type EligibleTemplate = { id: string; name: string };
type PropertyEnvironmentOption = {
  id: string;
  name: string;
  type: 'permanent' | 'ephemeral' | 'local' | 'sandbox';
  isDefault: boolean;
  url: string | null;
  sandboxV2Environment: string | null;
};

type PropertyListItem = {
  id: string;
  name: string;
  baseUrl: string | null;
  defaultAdHocEnvironmentTemplateId: string | null;
};

const FUZZ_MODE_OPTION_LABEL = 'Fuzzer (find breakage)';
const FUZZ_DEFAULT_FOCUS = 'Attack the whole application.';
const EFFORT_LABEL: Record<ExploratoryEffort, string> = {
  quick: 'Quick',
  standard: 'Standard',
  deep: 'Deep',
};

const LIVE_FALLBACK_TARGET_KEY = 'live:default';
const LIVE_TARGET_PREFIX = 'live:';
const SANDBOX_TARGET_PREFIX = 'sandbox:';
const SANDBOX_V2_TARGET_PREFIX = 'sandboxv2:';

const liveTargetKey = (environmentId: string) => `${LIVE_TARGET_PREFIX}${environmentId}`;
const sandboxTargetKey = (templateId: string) => `${SANDBOX_TARGET_PREFIX}${templateId}`;
const sandboxV2TargetKey = (environment: string) => `${SANDBOX_V2_TARGET_PREFIX}${environment}`;

export function StartExploratoryTestDialog({
  isOpen,
  onOpenChange,
  defaultFlavor = 'functional',
  lockFlavor = false,
}: StartExploratoryTestDialogProps) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const startMutation = useStartExploratoryTest();
  const { propertyOptions, propertiesQuery } = useOrgPropertySelection();

  const [selectedPropertyId, setSelectedPropertyId] = React.useState<string | null>(null);
  // Selected target. `null` means "use the property default" until the user
  // explicitly toggles the selector via the Change affordance.
  const [selectedTargetKey, setSelectedTargetKey] = React.useState<string | null>(null);
  const [envPickerOpen, setEnvPickerOpen] = React.useState(false);
  const [selectedCredentialId, setSelectedCredentialId] = React.useState<string | null>(null);
  const [provisionTempIdentity, setProvisionTempIdentity] = React.useState(false);
  const [instructions, setInstructions] = React.useState('');
  const [startUrl, setStartUrl] = React.useState('');
  const [contextDocs, setContextDocs] = React.useState<ContextDocument[]>([]);
  const [flavor, setFlavor] = React.useState<ReviewFlavor>(defaultFlavor);
  const [effort, setEffort] = React.useState<ExploratoryEffort | null>(
    FLAVOR_CAPABILITIES[defaultFlavor].effort?.default ?? null
  );
  const [browserEngine, setBrowserEngine] = React.useState<BrowserEngine>(DEFAULT_BROWSER_ENGINE);
  // Exploratory tests historically start at the desktop viewport. Keep that
  // default independent from the organization setting used by workflow runs.
  const [viewportSize, setViewportSize] = React.useState<BrowserViewportPreset>('desktop');
  const [errors, setErrors] = React.useState<Record<string, string>>({});
  const flavorMeta = getWebReviewFlavor(flavor);
  const capabilities = FLAVOR_CAPABILITIES[flavor];
  const usesCredentialInventory = capabilities.credentials === 'inventory';
  const supportsSandbox = capabilities.supportedTargets.includes('sandbox');
  const supportsSandboxV2 = capabilities.supportedTargets.includes('sandbox-v2-environment');
  const supportsLive = capabilities.supportedTargets.includes('live');
  const offersTargetPicker = supportsSandbox || supportsSandboxV2;
  // Flavors with an effort capability (the fuzzer) have no required prompt: the
  // instructions become an optional focus.
  const focusIsOptional = capabilities.effort !== null;

  // The Mode select only offers review flavors whose feature flag is on for
  // this org — flag-off users must not see a mode that only fails after
  // submit. `lockFlavor` entries (Labs routes) are already behind a
  // RequireFeature guard for their own flavor, so they're trusted, but the
  // Mode select itself is hidden whenever `lockFlavor` is set.
  const availableReviewFlavors = WEB_REVIEW_FLAVOR_LIST.filter((meta) =>
    Boolean(user?.features?.[meta.featureKey])
  );
  const modeItems: SelectOption[] = [
    { id: 'functional', label: 'Functional (pass/fail QA)' },
    ...availableReviewFlavors.map((meta) => ({ id: meta.key, label: meta.modeOptionLabel })),
    ...(user?.features?.fuzzExploratory ? [{ id: 'fuzz', label: FUZZ_MODE_OPTION_LABEL }] : []),
  ];
  const showModeSelect = !lockFlavor && modeItems.length > 1;
  const lockedMeta = lockFlavor ? flavorMeta : null;

  // Fetch the full property objects so we can read the per-property default
  // ad-hoc env template id. The propertyOptions hook only carries
  // {id, label} for the picker.
  const propertiesFullQ = useQuery<{ ok: true; items: PropertyListItem[] }>({
    queryKey: ['properties', 'full'],
    queryFn: async () => {
      const r = await fetch(`${API_URL}/org/properties`, { credentials: 'include' });
      if (!r.ok) throw new Error('Failed to fetch properties');
      return r.json();
    },
  });
  const fullProperties = propertiesFullQ.data?.items ?? [];
  const selectedProperty = fullProperties.find((p) => p.id === selectedPropertyId) ?? null;

  // Eligible verified sandbox templates for the selected property. Only
  // fetched once a property is chosen so we don't churn for users with
  // many properties.
  const templatesQ = useQuery<EligibleTemplate[]>({
    queryKey: ['environment-templates', 'ad-hoc-eligible', selectedPropertyId],
    enabled: Boolean(selectedPropertyId),
    queryFn: async () => {
      const r = await fetch(
        `${API_URL}/org/environments/templates?eligibleFor=ad_hoc_testing&propertyId=${selectedPropertyId}&limit=100`,
        { credentials: 'include' }
      );
      if (!r.ok) throw new Error('Failed to fetch templates');
      const json = await r.json();
      return (json.data ?? []) as EligibleTemplate[];
    },
  });

  const environmentsQ = useQuery<{ ok: true; items: PropertyEnvironmentOption[] }>({
    queryKey: ['property-environments', selectedPropertyId],
    enabled: Boolean(selectedPropertyId),
    queryFn: async () => {
      const r = await fetch(`${API_URL}/org/properties/${selectedPropertyId}/environments`, {
        credentials: 'include',
      });
      if (!r.ok) throw new Error('Failed to fetch environments');
      return r.json();
    },
  });

  React.useEffect(() => {
    if (isOpen) {
      setSelectedPropertyId(null);
      setSelectedTargetKey(null);
      setEnvPickerOpen(false);
      setSelectedCredentialId(null);
      setInstructions('');
      setStartUrl('');
      setContextDocs([]);
      setFlavor(defaultFlavor);
      setEffort(FLAVOR_CAPABILITIES[defaultFlavor].effort?.default ?? null);
      setBrowserEngine(DEFAULT_BROWSER_ENGINE);
      setViewportSize('desktop');
      setErrors({});
    }
  }, [isOpen]);

  const propertyEnvironments = environmentsQ.data?.items ?? [];
  const liveEnvironments = React.useMemo(
    () =>
      propertyEnvironments.filter(
        (environment) =>
          environment.type !== 'sandbox' &&
          environment.type !== 'local' &&
          !environment.sandboxV2Environment
      ),
    [propertyEnvironments]
  );
  const sandboxV2Environments = React.useMemo(
    () => propertyEnvironments.filter((environment) => environment.sandboxV2Environment),
    [propertyEnvironments]
  );
  const defaultLiveEnvironment =
    liveEnvironments.find((environment) => environment.isDefault) ?? liveEnvironments[0] ?? null;
  const firstSandboxV2Environment = sandboxV2Environments[0]?.sandboxV2Environment ?? null;
  const defaultTargetKey =
    supportsSandbox && selectedProperty?.defaultAdHocEnvironmentTemplateId
      ? sandboxTargetKey(selectedProperty.defaultAdHocEnvironmentTemplateId)
      : !supportsLive && firstSandboxV2Environment
        ? sandboxV2TargetKey(firstSandboxV2Environment)
        : defaultLiveEnvironment
          ? liveTargetKey(defaultLiveEnvironment.id)
          : LIVE_FALLBACK_TARGET_KEY;
  const effectiveTargetKey = selectedTargetKey ?? defaultTargetKey;
  const effectiveSandboxTemplateId = effectiveTargetKey.startsWith(SANDBOX_TARGET_PREFIX)
    ? effectiveTargetKey.slice(SANDBOX_TARGET_PREFIX.length)
    : null;
  const effectiveSandboxV2Environment = effectiveTargetKey.startsWith(SANDBOX_V2_TARGET_PREFIX)
    ? effectiveTargetKey.slice(SANDBOX_V2_TARGET_PREFIX.length)
    : null;
  const effectiveTemplate = effectiveSandboxTemplateId
    ? (templatesQ.data?.find((t) => t.id === effectiveSandboxTemplateId) ?? null)
    : null;
  const effectiveLiveEnvironmentId = effectiveTargetKey.startsWith(LIVE_TARGET_PREFIX)
    ? effectiveTargetKey.slice(LIVE_TARGET_PREFIX.length)
    : null;
  const effectiveLiveEnvironment = effectiveLiveEnvironmentId
    ? (liveEnvironments.find((environment) => environment.id === effectiveLiveEnvironmentId) ??
      null)
    : null;
  const instructionLength = getExploratoryInstructionLength(instructions);
  const instructionsTooLong = instructionLength > ADHOC_TEST_INSTRUCTIONS_MAX_LENGTH;
  const visibleInstructionsError =
    errors.instructions ||
    (instructionsTooLong
      ? (getExploratoryInstructionsError(instructions) ?? undefined)
      : undefined);

  const envOptions: SelectOption[] = React.useMemo(() => {
    const liveOptions = (supportsLive ? liveEnvironments : []).map((environment) => ({
      id: liveTargetKey(environment.id),
      label: environment.isDefault ? `${environment.name} (default)` : environment.name,
      description: environment.url ?? undefined,
    }));
    const sandboxOptions = (supportsSandbox ? (templatesQ.data ?? []) : []).map((template) => ({
      id: sandboxTargetKey(template.id),
      label: `${template.name} (sandbox)`,
    }));

    const sandboxV2Options = sandboxV2Environments.flatMap((environment) =>
      environment.sandboxV2Environment
        ? [
            {
              id: sandboxV2TargetKey(environment.sandboxV2Environment),
              label: `${environment.name} (sandbox v2)`,
              description: 'Sandbox environment (agent picks personas)',
            },
          ]
        : []
    );

    return [
      ...(!supportsLive
        ? []
        : liveOptions.length > 0
          ? liveOptions
          : [{ id: LIVE_FALLBACK_TARGET_KEY, label: 'Live application URL' }]),
      ...sandboxOptions,
      ...sandboxV2Options,
    ];
  }, [liveEnvironments, sandboxV2Environments, templatesQ.data, supportsLive, supportsSandbox]);
  const hasEligibleTemplates = (templatesQ.data?.length ?? 0) > 0;

  const handleSubmit = () => {
    const newErrors: Record<string, string> = {};
    // The fuzzer's focus is optional; the API still needs non-empty instructions.
    const submittedInstructions =
      focusIsOptional && !instructions.trim() ? FUZZ_DEFAULT_FOCUS : instructions.trim();
    const instructionError = getExploratoryInstructionsError(submittedInstructions);
    if (!selectedPropertyId) newErrors.property = 'Application is required';
    if (!supportsLive && !effectiveSandboxV2Environment) {
      newErrors.environment = 'Select a sandbox environment';
    }
    if (instructionError) newErrors.instructions = instructionError;
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // Review flavors (UX, spec) are live-only in this MVP (no sandbox planner
    // phase) — their capabilities exclude the sandbox targets.
    const isSandboxTarget = supportsSandbox && effectiveSandboxTemplateId !== null;
    const isSandboxV2Target = supportsSandboxV2 && effectiveSandboxV2Environment !== null;
    const target = isSandboxV2Target
      ? { kind: 'sandbox-v2-environment' as const, environment: effectiveSandboxV2Environment }
      : isSandboxTarget
      ? { kind: 'sandbox' as const, environmentTemplateId: effectiveSandboxTemplateId }
      : {
          kind: 'live' as const,
          credentialId: usesCredentialInventory ? null : selectedCredentialId,
          environmentId: effectiveLiveEnvironment?.id ?? null,
          // UX sign-up walkthrough: only when no credential is chosen.
          provisionTempIdentity:
            capabilities.credentials === 'tempIdentity' && !selectedCredentialId
              ? provisionTempIdentity
              : false,
        };
    const submittedStartUrl =
      startUrl.trim() || (target.kind === 'live' ? (effectiveLiveEnvironment?.url ?? '') : '');

    startMutation.mutate(
      {
        propertyId: selectedPropertyId!,
        instructions: submittedInstructions,
        startUrl: isSandboxV2Target ? null : submittedStartUrl || null,
        target,
        flavor,
        ...(capabilities.effort && effort ? { effort } : {}),
        browserEngine,
        viewportSize,
        // Legacy field for older clients/middleware; backend prefers `target`.
        credentialId: target.kind === 'live' && !usesCredentialInventory ? selectedCredentialId : null,
        contextDocumentIds: isSandboxV2Target ? [] : contextDocs.map((d) => d.id),
      },
      {
        onSuccess: (data) => {
          onOpenChange(false);
          // Review flavors run live in their Labs list; functional runs in
          // Exploratory.
          const detailPath: ReviewFlavorDetailPath | '/exploratory/$exploratoryTestId' =
            flavorMeta?.detailPath ?? '/exploratory/$exploratoryTestId';
          navigate({
            to: detailPath,
            params: { exploratoryTestId: data.adhocTestId },
          });
        },
        onError: (error) => {
          setErrors((prev) => ({
            ...prev,
            submit: error instanceof Error ? error.message : 'Failed to start exploratory test',
          }));
        },
      }
    );
  };

  // The picker is hidden by default when the property has a default env
  // configured (zero-click flow). User clicks "Change" to override.
  const showEnvPicker =
    envPickerOpen ||
    !selectedProperty ||
    !supportsSandbox ||
    !selectedProperty.defaultAdHocEnvironmentTemplateId;

  return (
    <DialogTrigger isOpen={isOpen} onOpenChange={onOpenChange} size="2xl">
      <span />
      <Dialog
        title={lockedMeta ? lockedMeta.dialogTitle : 'New Exploratory Test'}
        eyebrow={lockedMeta ? lockedMeta.dialogEyebrow : 'Exploratory test'}
        description={
          lockedMeta
            ? lockedMeta.dialogDescription
            : 'The agent follows your instructions, flags issues for verification, and produces a summary report with video recording.'
        }
        footer={
          <>
            <Button variant="ghost" onPress={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onPress={handleSubmit}
              isPending={startMutation.isPending}
              isDisabled={
                !selectedPropertyId ||
                (!focusIsOptional && !instructions.trim()) ||
                instructionsTooLong ||
                (!supportsLive && !effectiveSandboxV2Environment)
              }
            >
              Start Test
            </Button>
          </>
        }
      >
        <DialogContent>
          <Flex direction="column" gap="md">
            {showModeSelect && (
              <Flex gap="sm">
                <Box className="flex-1">
                  <Select
                    label="Mode"
                    items={modeItems}
                    selectedKey={flavor}
                    onSelectionChange={(key) => {
                      const id = key ? String(key) : 'functional';
                      const next: ReviewFlavor =
                        id === 'ux' || id === 'spec' || id === 'fuzz' ? id : 'functional';
                      setFlavor(next);
                      setEffort(FLAVOR_CAPABILITIES[next].effort?.default ?? null);
                      // Targets differ per flavor, so a key picked under the
                      // previous mode may no longer be offered.
                      setSelectedTargetKey(null);
                      setSelectedCredentialId(null);
                      setProvisionTempIdentity(false);
                      setErrors({});
                    }}
                  />
                </Box>
              </Flex>
            )}

            <Box>
              <Text size="sm" weight="medium">
                Browser engine
              </Text>
              <BrowserEnginePicker
                value={browserEngine}
                onChange={setBrowserEngine}
                aria-label="Browser engine"
              />
            </Box>

            <Box>
              <Text size="sm" weight="medium">
                Viewport size
              </Text>
              <ViewportPicker
                value={viewportSize}
                onChange={setViewportSize}
                aria-label="Viewport size"
              />
              <Text size="xs" className="text-muted-foreground mt-1">
                Choose the browser size for this test. Exploratory tests start at the desktop
                default.
              </Text>
            </Box>

            {capabilities.effort && effort && (
              <Box>
                <Text size="sm" weight="medium">
                  Effort
                </Text>
                <RadioGroup
                  aria-label="Effort"
                  value={effort}
                  onChange={(next) => setEffort(next as ExploratoryEffort)}
                  className="mt-1 flex flex-wrap gap-2"
                >
                  {EXPLORATORY_EFFORT_VALUES.map((value) => (
                    <Radio
                      key={value}
                      value={value}
                      className={({ isSelected, isFocusVisible }) =>
                        cn(
                          'flex cursor-pointer flex-col gap-0.5 rounded-lg border px-3 py-2 transition-colors',
                          'hover:bg-muted/40',
                          isSelected
                            ? 'border-primary bg-primary/5 ring-1 ring-primary'
                            : 'border-border bg-card',
                          isFocusVisible && 'outline-none ring-2 ring-primary ring-offset-1'
                        )
                      }
                    >
                      <span className="text-sm font-medium text-foreground">
                        {EFFORT_LABEL[value]}
                      </span>
                      <span className="text-xs tabular-nums text-muted-foreground">
                        about {EXPLORATORY_EFFORT_MINUTES[value]} min
                      </span>
                    </Radio>
                  ))}
                </RadioGroup>
              </Box>
            )}

            <Box>
              <Select
                label="Application"
                placeholder="Select a application..."
                items={propertyOptions}
                selectedKey={selectedPropertyId}
                onSelectionChange={(key) => {
                  setSelectedPropertyId(key ? String(key) : null);
                  setSelectedTargetKey(null);
                  setEnvPickerOpen(false);
                  setSelectedCredentialId(null);
                  setErrors((prev) => ({ ...prev, property: '' }));
                }}
                isDisabled={propertiesQuery.isLoading || propertiesFullQ.isLoading}
                isRequired
              />
              {errors.property && (
                <Text size="xs" className="text-destructive mt-1">
                  {errors.property}
                </Text>
              )}
            </Box>

            {selectedPropertyId && offersTargetPicker && (
              <Box>
                {showEnvPicker ? (
                  <Flex direction="column" gap="sm">
                    <Select
                      label="Environment"
                      items={envOptions}
                      selectedKey={effectiveTargetKey}
                      onSelectionChange={(key) => {
                        const value = key ? String(key) : LIVE_FALLBACK_TARGET_KEY;
                        setSelectedTargetKey(value);
                        // Clear credential when targeting a sandbox — the action
                        // seeds one. Keep credential picker live for live target.
                        if (
                          value.startsWith(SANDBOX_TARGET_PREFIX) ||
                          value.startsWith(SANDBOX_V2_TARGET_PREFIX)
                        ) {
                          setSelectedCredentialId(null);
                        }
                      }}
                      isDisabled={templatesQ.isLoading || environmentsQ.isLoading}
                    />
                    {errors.environment && (
                      <Text size="xs" className="text-destructive">
                        {errors.environment}
                      </Text>
                    )}
                    {!supportsLive && !environmentsQ.isLoading && !firstSandboxV2Environment && (
                      <Text size="xs" className="text-muted-foreground">
                        The Fuzzer runs only in a sandbox environment, and this application has
                        none yet.
                      </Text>
                    )}
                    {supportsSandbox && !templatesQ.isLoading && !hasEligibleTemplates && (
                      <Text size="xs" className="text-muted-foreground">
                        No eligible sandbox templates are available for this application yet. Live
                        environments remain available.
                      </Text>
                    )}
                  </Flex>
                ) : (
                  <Flex align="center" justify="between" gap="sm">
                    <Text size="sm">
                      Targeting{' '}
                      <strong>
                        {effectiveTemplate?.name ??
                          effectiveSandboxV2Environment ??
                          effectiveLiveEnvironment?.name ??
                          'configured environment'}
                      </strong>
                    </Text>
                    <Button variant="secondary" size="sm" onPress={() => setEnvPickerOpen(true)}>
                      Change
                    </Button>
                  </Flex>
                )}
              </Box>
            )}

            {selectedPropertyId &&
              !usesCredentialInventory &&
              supportsLive &&
              (!supportsSandbox ||
                (effectiveSandboxTemplateId === null && effectiveSandboxV2Environment === null)) && (
                <CredentialSelect
                  propertyId={selectedPropertyId}
                  selectedCredentialId={selectedCredentialId}
                  onSelect={(id) => {
                    setSelectedCredentialId(id);
                    setProvisionTempIdentity(false);
                  }}
                  label="Credential (optional)"
                  placeholder="No credential (anonymous)"
                  filterEnvironmentId={effectiveLiveEnvironment?.id}
                  // Some review flavors (UX) can provision a throwaway sign-up
                  // identity in place of a credential (mutually exclusive with
                  // picking one).
                  showProvisionOption={capabilities.credentials === 'tempIdentity'}
                  isProvisionSelected={provisionTempIdentity}
                  onSelectProvision={() => {
                    setSelectedCredentialId(null);
                    setProvisionTempIdentity(true);
                  }}
                />
              )}

            <Box>
              <TextArea
                label={
                  focusIsOptional
                    ? 'Focus (optional)'
                    : (flavorMeta?.instructionsLabel ?? 'Instructions')
                }
                placeholder={
                  focusIsOptional
                    ? 'Point the fuzzer at an area, e.g. "checkout and billing". Leave empty to attack the whole application.'
                    : (flavorMeta?.instructionsPlaceholder ??
                      'Describe what to test. Be specific about features, flows, and edge cases to cover...')
                }
                value={instructions}
                onChange={(value) => {
                  setInstructions(value);
                  setErrors((prev) => {
                    if (!prev.instructions && !prev.submit) return prev;
                    const next = { ...prev };
                    delete next.instructions;
                    delete next.submit;
                    return next;
                  });
                }}
                errorMessage={visibleInstructionsError}
              />
              <Flex align="center" justify="end" gap="sm" className="mt-1">
                <Text
                  size="xs"
                  className={instructionsTooLong ? 'text-destructive' : 'text-muted-foreground'}
                >
                  {instructionLength.toLocaleString()} / {getExploratoryInstructionsLimitLabel()}
                </Text>
              </Flex>
            </Box>

            {!effectiveSandboxV2Environment && (
            <TextField
              label="Start URL (optional)"
              placeholder={
                effectiveSandboxTemplateId
                  ? 'Defaults to the leased sandbox URL'
                  : effectiveLiveEnvironment
                    ? `Defaults to ${effectiveLiveEnvironment.name}`
                    : 'Defaults to application base URL'
              }
              value={startUrl}
              onChange={setStartUrl}
            />
            )}

            {!effectiveSandboxV2Environment && (
              <ContextLibraryField value={contextDocs} onChange={setContextDocs} />
            )}

            {errors.submit && (
              <Text size="sm" className="text-destructive">
                {errors.submit}
              </Text>
            )}
          </Flex>
        </DialogContent>
      </Dialog>
    </DialogTrigger>
  );
}
